import { useCallback, useEffect, useRef, useState } from 'react';
import { getCachedMedia, mediaCacheKey, putCachedMedia } from './mediaCache';

/**
 * Plays exact time ranges of one media element: checks the clock every animation frame,
 * so a sentence stops (or loops back) within a frame of its end time.
 * Call playRange() straight from a tap handler — iOS only allows play() inside a user gesture.
 */
export function useRangePlayer() {
  const el = useRef<HTMLVideoElement | null>(null);
  const range = useRef<{ start: number; end: number; loop: boolean } | null>(null);
  const [playing, setPlaying] = useState(false);
  const [loops, setLoops] = useState(1);
  const [rate, setRateState] = useState(1);

  useEffect(() => {
    if (!playing) return;
    let id = 0;
    const tick = () => {
      const v = el.current;
      const r = range.current;
      if (v && r && v.currentTime >= r.end) {
        if (r.loop) {
          v.currentTime = r.start;
          setLoops((n) => n + 1);
        } else {
          v.pause();
          range.current = null;
        }
      }
      id = requestAnimationFrame(tick);
    };
    id = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(id);
  }, [playing]);

  const playRange = useCallback((startMs: number, endMs: number, loop: boolean) => {
    const v = el.current;
    if (!v) return;
    // A little lead-in and tail so first and last syllables are not clipped.
    range.current = { start: Math.max(0, startMs - 60) / 1000, end: (endMs + 150) / 1000, loop };
    setLoops(1);
    v.currentTime = range.current.start;
    void v.play().catch(() => undefined);
  }, []);

  /** Pause keeps the sentence; play resumes inside it, or restarts it if it had finished. */
  const toggle = useCallback((fallback: () => void) => {
    const v = el.current;
    if (!v) return;
    if (!v.paused) return v.pause();
    const r = range.current;
    if (r && v.currentTime >= r.start && v.currentTime < r.end) void v.play().catch(() => undefined);
    else fallback();
  }, []);

  const setLoop = useCallback((loop: boolean) => {
    if (range.current) range.current.loop = loop;
  }, []);

  const stop = useCallback(() => {
    range.current = null;
    el.current?.pause();
  }, []);

  const setRate = useCallback((r: number) => {
    setRateState(r);
    if (el.current) el.current.playbackRate = r;
  }, []);

  const bind = {
    ref: el,
    onPlay: () => setPlaying(true),
    onPause: () => setPlaying(false),
    onEnded: () => setPlaying(false),
    onLoadedMetadata: () => {
      if (el.current) el.current.playbackRate = rate;
    },
  };
  return { bind, el, playing, loops, rate, playRange, toggle, setLoop, stop, setRate };
}

/**
 * Downloads a (small) audio file completely so every seek afterwards is instant, and keeps it on
 * the device so the next visit opens without downloading again.
 * Falls back to streaming the URL if the download fails.
 */
export function usePreloadedMedia(url: string | null) {
  const [src, setSrc] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    let objectUrl: string | null = null;
    setSrc(null);
    setProgress(0);
    const key = mediaCacheKey(url);
    (async () => {
      try {
        const cached = key ? await getCachedMedia(key) : null;
        if (cancelled) return;
        if (cached) {
          objectUrl = URL.createObjectURL(cached);
          setSrc(objectUrl);
          setProgress(1);
          return;
        }
        const res = await fetch(url);
        if (!res.ok || !res.body) throw new Error(String(res.status));
        const total = Number(res.headers.get('content-length')) || 0;
        const reader = res.body.getReader();
        const chunks: Uint8Array[] = [];
        let got = 0;
        for (;;) {
          const { done, value } = await reader.read();
          if (done || cancelled) break;
          chunks.push(value);
          got += value.length;
          if (total) setProgress(Math.min(0.99, got / total));
        }
        if (cancelled) return;
        const blob = new Blob(chunks as BlobPart[], { type: res.headers.get('content-type') ?? 'audio/mp4' });
        if (key && (!total || blob.size === total)) void putCachedMedia(key, blob);
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
        setProgress(1);
      } catch {
        if (!cancelled) {
          setSrc(url);
          setProgress(1);
        }
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url && (mediaCacheKey(url) ?? url)]); // a new daily link to the same file is not a new file
  return { src, progress };
}

/** A media URL, or the copy already saved on the device (from 精听) when there is one. */
export function useCachedMediaUrl(url: string) {
  const [src, setSrc] = useState(url);
  useEffect(() => {
    setSrc(url);
    const key = mediaCacheKey(url);
    if (!key) return;
    let objectUrl: string | null = null;
    let cancelled = false;
    void getCachedMedia(key).then((blob) => {
      if (cancelled || !blob) return;
      objectUrl = URL.createObjectURL(blob);
      setSrc(objectUrl);
    });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);
  return src;
}
